/**
 * Which state the budgets on this page are resolved in.
 *
 * ONE MACHINE AT A TIME, and that is a decision rather than a simplification.
 * The states of one machine are mutually exclusive by construction, so
 * comparing them is comparing alternatives. Two machines are two INDEPENDENT
 * axes - a vehicle can be in Standby and its GNC in Fine at the same time - so
 * a figure taken across both would be evaluating scenarios that are each half
 * a scenario, and the worst of those is not the worst case. Patrick,
 * 2026-09-08.
 *
 * The baseline is always offered and is always the default. It is not a state:
 * it is what holds wherever nothing else is said, and a page that opened on
 * some arbitrary state would be answering a question nobody asked.
 */

import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { EntityRow } from '../../schema'
import { naturalCompare } from '../common/naturalCompare'

export interface StateOption {
  id: string
  label: string
  machine: string
}

/**
 * The machines a worst case can be taken over.
 *
 * A machine qualifies only if it CONTAINS states. One with none has nothing
 * to be a worst case over, and the server refuses it - so offering it would
 * be building a control whose only outcome is an error.
 */
export function machineOptions(rows: EntityRow[]): StateOption[] {
  const owners = new Set(rows.map((row) => row.owner).filter(Boolean).map(String))
  return rows
    .filter((row) => owners.has(row.id))
    .map((row) => ({
      id: row.id,
      label: String(row.name ?? row.id),
      machine: String(row.name ?? row.id),
    }))
    .sort((a, b) => naturalCompare(a.label, b.label))
}

/** The value the picker holds: baseline, one state, or one machine's worst. */
export const WORST_PREFIX = 'worst:'

/**
 * Split the picker's value into what the API takes.
 *
 * One function rather than the caller branching, so the "never both" rule the
 * server enforces cannot be broken on this side by an intermediate state
 * where two pieces of component state disagree.
 */
export function scopeOf(value: string): { state?: string; worstOf?: string } {
  if (value.startsWith(WORST_PREFIX)) {
    return { worstOf: value.slice(WORST_PREFIX.length) }
  }
  return value ? { state: value } : {}
}

/**
 * The selectable states, grouped under the machine that declares them.
 *
 * Exported for its own test. Only states that BELONG to a machine are offered,
 * and the reason is the one above: a state nothing contains has no siblings to
 * be mutually exclusive with, so picking it compares it against nothing.
 *
 * A machine with no sub-states is therefore absent rather than listed empty.
 */
export function stateOptions(rows: EntityRow[]): StateOption[] {
  const names = new Map<string, string>()
  for (const row of rows) names.set(row.id, String(row.name ?? row.id))

  return rows
    .filter((row) => Boolean(row.owner))
    .map((row) => ({
      id: row.id,
      label: String(row.name ?? row.id),
      machine: names.get(String(row.owner)) ?? String(row.owner),
    }))
    .sort((a, b) =>
      naturalCompare(a.machine, b.machine) || naturalCompare(a.label, b.label))
}

export default function StatePicker({
  options,
  machines,
  value,
  onChange,
}: {
  options: StateOption[]
  machines: StateOption[]
  value: string
  onChange: (next: string) => void
}) {
  if (options.length === 0) return null

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <TextField
        select
        size="small"
        label="Resolved in"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        // displayEmpty, because '' is a REAL choice here and not an absent
        // one. Without it MUI treats the empty string as "nothing selected"
        // and renders a blank box, so the page's default state - the baseline,
        // which is what it is actually showing - reads as unset.
        slotProps={{ select: { displayEmpty: true } }}
        sx={{ minWidth: 220 }}
      >
        <MenuItem value="">Baseline</MenuItem>
        {machines.map((machine) => (
          <MenuItem key={machine.id} value={`${WORST_PREFIX}${machine.id}`}>
            {machine.label} — worst case
          </MenuItem>
        ))}
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.machine} — {option.label}
          </MenuItem>
        ))}
      </TextField>
      <Typography variant="body2" color="text.secondary">
        {value.startsWith(WORST_PREFIX)
          ? 'The worst of this machine’s states, per figure, each labelled with the state it came from.'
          : value
            ? 'Totals and budgets both resolved in this state.'
            : 'What holds wherever no state says otherwise.'}
      </Typography>
    </Box>
  )
}
